import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { SingleHoverDirective } from '@web/layout/content/page-content/directives/single-hover.directive';

import { AddButtonsComponent } from '@web/layout/content/page-content/components/add-buttons/add-buttons.component';
import type { DenormalizedDefaultBlockEntity } from '@web/types';
import { toUnsharedBlockMeta } from '@sites/entity-core';
import { BaseBlockComponent } from '@web/layout/content/page-content/components/blocks/base-block.abstract';
import { SharedBlockControlsComponent } from '@web/layout/content/page-content/components/controls/shared-block-controls/shared-block-controls.component';
import { DeleteSharedBlockModalComponent } from '@web/shared/modals/delete-shared-block-modal/delete-shared-block-modal.component';

import { DefaultBlockViewComponent } from '../default-block-view/default-block-view.component';

@Component({
    selector: 'web-shared-block',
    templateUrl: './shared-block.component.html',
    styleUrls: ['./shared-block.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SingleHoverDirective,
        TooltipModule,
        DefaultBlockViewComponent,
        AddButtonsComponent,
        SharedBlockControlsComponent,
        AsyncPipe,
        TranslateModule,
    ],
})
export class SharedBlockComponent extends BaseBlockComponent<DenormalizedDefaultBlockEntity> {
    private readonly bsModalService: BsModalService = inject(BsModalService);

    public readonly isEcommerceMode$ = this.appFacade.isEcommerceMode$;

    public copyBlock(): void {
        const block = {
            ...this.entity(),
            // The copy is an ordinary block, and it must not fight the original
            // for the pinned slot.
            props: { ...this.entity().props, pin: null },
            // The copy is no longer the shared instance, but it is still a header
            // or a footer — and `meta` has to be rebuilt rather than mutated,
            // since the spread above shares it with the entity in the store.
            meta: toUnsharedBlockMeta(this.entity().meta),
        };

        this.blocksFacade.copyBlock(block, this.index());
    }

    public removeBlock() {
        if (!this.entity().meta.isShared) {
            this.blocksFacade.removeBlock(this.entity(), this.index());
            return;
        }

        const modal = this.bsModalService.show(DeleteSharedBlockModalComponent);

        modal.content.onClose.subscribe((result: boolean) => {
            if (result) {
                this.blocksFacade.removeBlock(this.entity(), this.index());
            }
        });
    }
}
